import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { selectCid } from "../../features/companyid";
import { server_API } from "../../config";
import ReactApexChart from "react-apexcharts";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../axiosInstance";
import notification from "../../images/notification.png";
import conversion from "../../images/conversion_rate.png";
import diversity from "../../images/diversity.png";
import aging from "../../images/aging.png";
import {
  sortByOptions,
  SortByValue,
  sourceDonutLegends,
  defaultDepartment,
  defaultManagers,
} from "./helper";
import {
  getStartOfMonth,
  getEndOfMonth,
  getStartOfLastMonth,
  getEndOfLastMonth,
  getStartOfLast90Days,
  getEndOfLast90Days,
  getStartOfYearToDate,
  getEndOfYearToDate,
  getStartOfPreviousMonth,
  getEndOfPreviousMonth,
  getStartOfPrevious90Days,
  getEndOfPrevious90Days,
  getStartOfPreviousYearToDate,
  getEndOfPreviousYearToDate,
} from "../../utils/dateUtils";
import Switch from "react-switch";
import Cards from "./components/Cards";
import CandidateQuality from "./components/CandidateQuality";
import HiringPipeline from "./components/HiringPipeline";
import HiringPerformance from "./components/HiringPerformance";
import {
  getSpecialTab,
  getUserPrivilege,
  selectUuid,
  updateInternalSource,
  updateSpecialTab,
} from "../../features/userid";

const Dashboard = () => {
  const [departments, setDepartments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [toDoCount, setTodoCount] = useState(0);
  const [users, setUsers] = useState([]);
  const [offers, setOffers] = useState([]);
  const [counts, setCounts] = useState({
    "<7 Days": 0,
    "8-14 Days": 0,
    "15-30 Days": 0,
    "31-90 Days": 0,
    ">90 Days": 0,
  });
  const [sourceState, setSourceState] = useState({
    department: "",
    hiringManager: "",
  });
  const [agingState, setAgingState] = useState({
    department: "",
    hiringManager: "",
  });
  const [interviewsNotificationCount, setInterviewsNotificationCount] =
    useState(0);
  const userPrivilege = useSelector(getUserPrivilege);
  const userUuid = useSelector(selectUuid);
  const [approvalsCount, setApprovalCount] = useState(0);
  const [notificationstab, setNotificationstab] = useState(0);
  const companyId = useSelector(selectCid);
  const [allNotification, setAllNotification] = useState([]);
  const [companyDetails, setCompanyDetails] = useState({});
  const [hiringManagers, setHiringManagers] = useState([]);
  const [genderRatio, setGenderRatio] = useState([]);
  const [sortBy, setSortBy] = useState(
    () => JSON.parse(localStorage.getItem("sortBy")) ?? sortByOptions.at(0),
  );
  const [isHired, setIsHired] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    localStorage.setItem("sortBy", JSON.stringify(sortBy));
  }, [sortBy]);

  const navigate = useNavigate();

  useEffect(() => {
    if (companyId.length > 0) {
      axiosInstance
        .get(`v2/offer/getall/${companyId}`)
        .then((res) => {
          setOffers(res.data);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [companyId]);

  const [dashboardMetrics, setDashboardMetrics] = useState(null);
  const [dashboardMetricsKey, setDashboardMetricsKey] = useState(0);
  const [prevDashboardMetrics, setPrevDashboardMetrics] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchMetrics = async () => {
      if (!companyId) return;

      const today = new Date();
      let startDate, endDate, previousStartDate, previousEndDate;

      switch (sortBy?.value) {
        case SortByValue.CURRENT_MONTH:
          startDate = getStartOfMonth(today);
          endDate = getEndOfMonth(today);
          previousStartDate = getStartOfLastMonth(today);
          previousEndDate = getEndOfLastMonth(today);
          break;
        case SortByValue.LAST_MONTH:
          startDate = getStartOfLastMonth(today);
          endDate = getEndOfLastMonth(today);
          previousStartDate = getStartOfPreviousMonth(today, 2);
          previousEndDate = getEndOfPreviousMonth(today, 2);
          break;
        case SortByValue.LAST_90_DAYS:
          startDate = getStartOfLast90Days(today);
          endDate = getEndOfLast90Days(today);
          previousStartDate = getStartOfPrevious90Days(today);
          previousEndDate = getEndOfPrevious90Days(today);
          break;
        case SortByValue.YEAR_TO_DATE:
          startDate = getStartOfYearToDate(today);
          endDate = getEndOfYearToDate(today);
          previousStartDate = getStartOfPreviousYearToDate(today);
          previousEndDate = getEndOfPreviousYearToDate(today);
          break;
        case SortByValue.MONTH:
          startDate = new Date(today);
          startDate.setDate(today.getDate() - 29);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(today);
          endDate.setHours(23, 59, 59, 999);

          previousEndDate = new Date(startDate);
          previousEndDate.setDate(startDate.getDate() - 1);
          previousEndDate.setHours(23, 59, 59, 999);
          previousStartDate = new Date(previousEndDate);
          previousStartDate.setDate(previousEndDate.getDate() - 29);
          previousStartDate.setHours(0, 0, 0, 0);
          break;
        case SortByValue.WEEK:
        default:
          // "Last 7 days" = rolling 7 days (today-6 through today), not Mon-Sun
          startDate = new Date(today);
          startDate.setDate(today.getDate() - 6);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(today);
          endDate.setHours(23, 59, 59, 999);

          previousEndDate = new Date(startDate);
          previousEndDate.setDate(startDate.getDate() - 1);
          previousEndDate.setHours(23, 59, 59, 999);
          previousStartDate = new Date(previousEndDate);
          previousStartDate.setDate(previousEndDate.getDate() - 6);
          previousStartDate.setHours(0, 0, 0, 0);
          break;
      }

      const formatDate = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";

      try {
        const currentRes = await axiosInstance.get(
          `/v2/applicant/dashboard/metrics/${companyId}?start_date=${formatDate(startDate)}&end_date=${formatDate(endDate)}&tz=${encodeURIComponent(tz)}`,
        );
        setDashboardMetrics(currentRes.data);
        setDashboardMetricsKey((k) => k + 1);

        const prevRes = await axiosInstance.get(
          `/v2/applicant/dashboard/metrics/${companyId}?start_date=${formatDate(previousStartDate)}&end_date=${formatDate(previousEndDate)}&tz=${encodeURIComponent(tz)}`,
        );
        setPrevDashboardMetrics(prevRes.data);
      } catch (error) {
        console.error("Failed to fetch dashboard metrics", error);
      }
    };

    fetchMetrics();
  }, [companyId, sortBy]);

  const sourceDonutData = useMemo(() => {
    const labels = [
      "Campus campaign",
      "Company website",
      "Employees referral",
      "Naukri",
      "Indeed",
      "LinkedIn",
      "Recruitment Agency",
      "Others",
      "Northstarz",
    ];
    if (!dashboardMetrics?.source_metrics) {
      return { series: labels.map(() => 0), segmentSum: 0 };
    }
    const filtered = dashboardMetrics.source_metrics.filter((m) => {
      if (sourceState.department)
        return m.department === sourceState.department;
      return true;
    });
    const counts = filtered.reduce((acc, m) => {
      acc[m.source] = (acc[m.source] || 0) + m.count;
      return acc;
    }, {});
    const series = labels.map(
      (l) =>
        counts[l] ?? (l === "Northstarz" ? (counts["Northstarz.ai"] ?? 0) : 0),
    );
    const segmentSum = series.reduce((a, b) => a + b, 0);
    return { series, segmentSum };
  }, [dashboardMetrics?.source_metrics, sourceState.department]);

  const donutChart = {
    series: sourceDonutData.series,
    options: {
      chart: {
        type: "donut",
      },
      labels: [
        "Campus campaign",
        "Company website",
        "Employees referral",
        "Naukri",
        "Indeed",
        "LinkedIn",
        "Recruitment Agency",
        "Others",
        "Northstarz",
      ],
      colors: [
        "#17A3A0",
        "#E0726C",
        "#f8bc6c",
        "#656565",
        "#C175D4",
        "#C1D685",
        "#9D96E8",
        "#538EC9",
        "#1D2A3D",
      ],
      dataLabels: {
        dropShadow: {
          enabled: false,
        },
        formatter: function (val) {
          return Math.round(val) + "%";
        },
      },
      legend: {
        show: false,
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              total: {
                showAlways: true,
                show: true,
                formatter: function () {
                  return sourceDonutData.segmentSum;
                },
              },
            },
          },
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 300,
            },
            legend: {
              show: false,
            },
          },
        },
      ],
    },
  };

  const monochromeDonutChart = {
    series: genderRatio,
    options: {
      chart: {
        type: "donut",
      },
      labels: ["Male", "Female", "Others"],
      colors: ["#006FD6", "#538EC9", "#94CBFF"],
      dataLabels: {
        dropShadow: {
          enabled: false,
        },
        formatter: function (val) {
          return Math.round(val) + "%";
        },
      },
      legend: {
        show: true,
        position: "bottom",
        formatter: function (seriesName, opts) {
          const value = opts.w.globals.series[opts.seriesIndex];
          return `${seriesName} : ${value}`;
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "60%",
            labels: {
              show: true,
              name: {
                fontSize: "14px",
              },
              value: {
                fontSize: "22px",
                fontWeight: 600,
              },
              total: {
                showAlways: true,
                show: true,
                fontSize: "14px",
              },
            },
          },
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 300,
            },
            legend: {
              enabled: true,
              position: "bottom",
              display: true,
            },
          },
        },
      ],
    },
  };

  // Fetch aging counts from backend (MongoDB aggregation) - NOT timeline-based, shows all open+paused jobs
  useEffect(() => {
    const fetchAging = async () => {
      if (!companyId) return;
      const params = new URLSearchParams();
      if (agingState.department)
        params.set("department", agingState.department);
      if (agingState.hiringManager)
        params.set("hiring_manager", agingState.hiringManager);
      const qs = params.toString();
      const url = qs
        ? `/v2/job/dashboard/aging/${companyId}?${qs}`
        : `/v2/job/dashboard/aging/${companyId}`;
      try {
        const res = await axiosInstance.get(url);
        setCounts({
          "<7 Days": res.data["<7 Days"] ?? 0,
          "8-14 Days": res.data["8-14 Days"] ?? 0,
          "15-30 Days": res.data["15-30 Days"] ?? 0,
          "31-90 Days": res.data["31-90 Days"] ?? 0,
          ">90 Days": res.data[">90 Days"] ?? 0,
        });
      } catch (err) {
        console.error("Failed to fetch job aging metrics", err);
      }
    };
    fetchAging();
  }, [companyId, agingState.department, agingState.hiringManager]);

  const metricsJobCounts = useMemo(() => {
    return {
      positionCount: dashboardMetrics?.total_positions || 0,
      applicationCount: dashboardMetrics?.total_apps_overall || 0,
      hiredCount: dashboardMetrics?.total_hires_overall || 0,
      offerCount: dashboardMetrics?.total_offers_overall || 0,
      interviewCount: dashboardMetrics?.hiring_pipeline?.interview || 0,
    };
  }, [dashboardMetrics]);

  const prevMetricsJobCounts = useMemo(() => {
    return {
      positionCount: prevDashboardMetrics?.total_positions || 0,
      applicationCount: prevDashboardMetrics?.total_apps_overall || 0,
      hiredCount: prevDashboardMetrics?.total_hires_overall || 0,
      offerCount: prevDashboardMetrics?.total_offers_overall || 0,
      interviewCount: prevDashboardMetrics?.hiring_pipeline?.interview || 0,
    };
  }, [prevDashboardMetrics]);

  const openJobsCounts = useMemo(() => {
    return {
      positionCount: dashboardMetrics?.open_positions || 0,
    };
  }, [dashboardMetrics]);

  const previousOpenJobCounts = useMemo(() => {
    return {
      positionCount: prevDashboardMetrics?.open_positions || 0,
    };
  }, [prevDashboardMetrics]);

  const Chart = {
    series: [
      {
        name: "No of jobs",
        data: [
          counts["<7 Days"],
          counts["8-14 Days"],
          counts["15-30 Days"],
          counts["31-90 Days"],
          counts[">90 Days"],
        ],
      },
    ],

    options: {
      chart: {
        type: "bar",
        height: 350,
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          barHeight: "50%",
          distributed: true,
        },
      },
      colors: ["#4397E4"],
      dataLabels: {
        enabled: false,
        dropShadow: {
          enabled: true,
        },
      },

      xaxis: {
        categories: [
          "<7 Days",
          "8-14 Days",
          "15-30 Days",
          "31-90 Days",
          ">90 Days",
        ],
      },
      legend: {
        show: false,
      },
    },
  };

  useEffect(() => {
    if (notificationstab == 0) {
      setNotifications(
        allNotification.filter(
          (notification) =>
            notification.notificationtype !== "" ||
            notification.notificationtype === "todo",
        ),
      );
    } else if (notificationstab == 1) {
      setNotifications(
        allNotification.filter(
          (notification) => notification.notificationtype === "Interview",
        ),
      );
    } else if (notificationstab == 2) {
      setNotifications(
        allNotification.filter(
          (notification) => notification.notificationtype === "Approval",
        ),
      );
    }

    setTodoCount(
      allNotification.filter(
        (notification) =>
          notification.notificationtype !== "" ||
          notification.notificationtype === "todo",
      ).length,
    );
    setInterviewsNotificationCount(
      allNotification.filter(
        (notification) => notification.notificationtype === "Interview",
      ).length,
    );
    setApprovalCount(
      allNotification.filter(
        (notification) => notification.notificationtype === "Approval",
      ).length,
    );
  }, [allNotification, notificationstab]);

  useEffect(() => {
    if (companyDetails) {
      let dep = companyDetails?.departments?.map((d) => {
        return { value: d, label: d };
      });
      dep = [defaultDepartment].concat(dep);
      setDepartments(dep);
    }
  }, [companyDetails]);

  useEffect(() => {
    window.scrollTo(0, 0);
    axiosInstance
      .get(`/v2/company/get/${companyId}`)
      .then((response) => {
        setCompanyDetails(response.data);
        dispatch(updateSpecialTab(response.data.sourcing || false));
        dispatch(updateInternalSource(response.data.internal_source || false));
      })
      .catch((error) => {
        console.log(error);
      });

    axios
      .get(`${server_API}/v2/user/company/${companyId}`)
      .then((response) => {
        setUsers(response.data.message.Users);
      })
      .catch((error) => {
        console.log(error);
      });
    axiosInstance
      .get(`/v2/notification/getusernotification/${companyId}`, {
        withCredentials: true,
      })
      .then((res) => {
        setAllNotification(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  useEffect(() => {
    if (users.length > 0) {
      const u = users.filter((u) => {
        return (
          u.companyid == companyId &&
          u.privilege == "Hiring Manager" &&
          u.status == "accepted"
        );
      });
      const hms = [
        defaultManagers,
        ...u.map((h) => ({ value: h.uuid, label: h.name })),
      ];
      setHiringManagers(hms);
    }
  }, [users]);

  const [hiringPerformanceData, setHiringPerformanceData] = useState({
    hired: [],
    offered: [],
  });

  useEffect(() => {
    const fetchHiringPerformance = async () => {
      try {
        const res = await axiosInstance.get(
          `/v2/applicant/dashboard/hiring-performance/${companyId}`,
        );
        setHiringPerformanceData(res.data);
      } catch (error) {
        console.error("Failed to fetch hiring performance metrics", error);
      }
    };
    if (companyId) {
      fetchHiringPerformance();
    }
  }, [companyId]);

  const [diversityMetrics, setDiversityMetrics] = useState([]);
  useEffect(() => {
    const fetchDiversity = async () => {
      if (!companyId) return;

      const today = new Date();
      let startDate, endDate;

      switch (sortBy?.value) {
        case SortByValue.CURRENT_MONTH:
          startDate = getStartOfMonth(today);
          endDate = getEndOfMonth(today);
          break;
        case SortByValue.LAST_MONTH:
          startDate = getStartOfLastMonth(today);
          endDate = getEndOfLastMonth(today);
          break;
        case SortByValue.LAST_90_DAYS:
          startDate = getStartOfLast90Days(today);
          endDate = getEndOfLast90Days(today);
          break;
        case SortByValue.YEAR_TO_DATE:
          startDate = getStartOfYearToDate(today);
          endDate = getEndOfYearToDate(today);
          break;
        case SortByValue.MONTH:
          startDate = new Date(today);
          startDate.setDate(today.getDate() - 29);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(today);
          endDate.setHours(23, 59, 59, 999);
          break;
        case SortByValue.WEEK:
        default:
          startDate = new Date(today);
          startDate.setDate(today.getDate() - 6);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(today);
          endDate.setHours(23, 59, 59, 999);
          break;
      }

      const fmt = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      };

      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";

      try {
        const res = await axiosInstance.get(
          `/v2/applicant/dashboard/diversity/${companyId}?start_date=${fmt(startDate)}&end_date=${fmt(endDate)}&tz=${encodeURIComponent(tz)}`,
        );
        setDiversityMetrics(res.data || []);
      } catch (err) {
        console.error("Failed to fetch diversity metrics", err);
        setDiversityMetrics([]);
      }
    };
    fetchDiversity();
  }, [companyId, sortBy]);

  useEffect(() => {
    const gender = [0, 0, 0];
    if (diversityMetrics?.length) {
      diversityMetrics.forEach((m) => {
        const count = isHired ? m.hired_count : m.offer_count + m.hired_count;
        const g = m.gender?.toLowerCase() || "";
        if (g.startsWith("m")) gender[0] += count;
        else if (g.startsWith("f")) gender[1] += count;
        else gender[2] += count;
      });
    }
    setGenderRatio(gender);
  }, [diversityMetrics, isHired]);

  const handleNotificationClick = (notification) => {
    if (notification.notificationtype == "") return;
    const data = JSON.parse(notification.subtask);
    if (notification.notificationtype == "todo") {
      navigate("/jobs/candidates/about", {
        state: { uuid: data.id, offerletterpending: true },
      });
    }
    if (notification.notificationtype == "Interview") {
      navigate("/jobs/candidates/about", { state: { uuid: data.id } });
    } else if (notification.notificationtype == "Approval") {
      navigate("/jobs/candidates/about", {
        state: { uuid: data.id, proposalId: data.docid },
      });
    }
  };

  console.log(allNotification);
  return (
    <div className="bg-[#f0f4fc] min-h-screen">
      <div className="md:px-6 py-10 pt-5 p-3 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <p className="text-2xl font-semibold">Dashboard</p>
          <div className="relative">
            <Select
              value={sortBy}
              options={sortByOptions}
              onChange={(option) => setSortBy(option)}
              placeholder="Select Sort by"
              className="min-w-[150px]"
              theme={(theme) => ({
                ...theme,
                borderRadius: 0,
                colors: {
                  ...theme.colors,
                  primary25: "#94CBFF",
                  primary: "#006FD6",
                  neutral20: "#148EFF",
                  neutral30: "#148EFF",
                },
              })}
              styles={{
                control: (styles) => ({
                  ...styles,
                  backgroundColor: "#fff",
                  cursor: "pointer",
                  borderRadius: "6px",
                  textAlign: "center",
                }),
                indicatorSeparator: (styles) => ({
                  ...styles,
                  backgroundColor: "#fff",
                }),
                input: (styles) => ({
                  ...styles,
                  color: "#148EFF",
                }),
                singleValue: (styles) => ({
                  ...styles,
                  color: "#148EFF",
                }),
                container: (styles) => ({
                  ...styles,
                  backgroundColor: "",
                }),
                dropdownIndicator: (styles) => ({
                  ...styles,
                  color: "#148EFF",
                }),
                menu: (styles) => ({
                  ...styles,
                  borderRadius: "6px",
                  backgroundColor: "#148EFF",
                  border: "",
                }),
                menuList: (styles) => ({
                  ...styles,
                  border: "",
                  borderRadius: "6px",
                }),
                option: (styles) => ({
                  ...styles,
                  cursor: "pointer",
                  color: "#fff",
                  textAlign: "center",
                }),
              }}
            />
          </div>
        </div>
        <Cards
          jobCounts={metricsJobCounts}
          previousJobCounts={prevMetricsJobCounts}
          openJobsCounts={openJobsCounts}
          previousOpenJobCounts={previousOpenJobCounts}
          isDiffNA={!prevDashboardMetrics}
        />
        <div className="flex w-full my-5 space-x-10 ">
          <div className="flex flex-col space-y-10 w-1/2">
            <HiringPipeline
              departments={departments}
              hiringManagers={hiringManagers}
              pipelineData={dashboardMetrics?.applications_per_job || []}
              userPrivilege={userPrivilege}
            />
            <CandidateQuality
              pipelineData={dashboardMetrics?.candidate_quality_per_job || []}
              previousPipelineData={
                prevDashboardMetrics?.candidate_quality_per_job || []
              }
              departments={departments}
              hiringManagers={hiringManagers}
              userPrivilege={userPrivilege}
            />
          </div>
          <div className="flex flex-col space-y-5 w-1/2">
            <div className=" bg-white  h-[36rem] mb-4 py-3 items-center  rounded-divRadius shadow-sh">
              <div className="flex items-center justify-between w-full">
                <h2 className="font-semibold  relative  text-[16px] pl-4 leading-[18.75px] before:block before:bg-blue-500 before:w-1 before:content-'' before:absolute before:left-0 before:top-1/2 before:h-2/4 before:mr-4 before:transform before:-translate-y-1/2">
                  My Actions
                </h2>
                <div className="flex invisible justify-end space-x-3 pr-4">
                  <Select className="text-sm"></Select>
                  <Select className="text-sm"></Select>
                </div>
              </div>
              <div className="bg-gray-300 h-[1px] mt-3 w-full"></div>
              <div className="mx-4 my-6">
                <div className="px-[10px] py-[10px] rounded-lg flex items-center justify-between  bg-[#F1F4FB]">
                  <div
                    className={
                      notificationstab == 0
                        ? "bg-web-blue text-white py-[11px] text-center rounded-lg  w-1/3 cursor-pointer"
                        : "font-semibold w-1/3 text-center py-[11px] cursor-pointer"
                    }
                    onClick={() => {
                      setNotificationstab(0);
                    }}
                  >
                    To-Do - {toDoCount}
                  </div>
                  <div
                    className={
                      notificationstab == 1
                        ? "bg-web-blue text-white py-[11px] text-center rounded-lg  w-1/3 cursor-pointer"
                        : "font-semibold w-1/3 text-center py-[11px] cursor-pointer"
                    }
                    onClick={() => {
                      setNotificationstab(1);
                    }}
                  >
                    Interviews - {interviewsNotificationCount}
                  </div>
                  <div
                    className={
                      notificationstab == 2
                        ? "bg-web-blue text-white py-[11px] text-center rounded-lg  w-1/3 cursor-pointer"
                        : " w-1/3  font-semibold text-center py-[11px] cursor-pointer"
                    }
                    onClick={() => {
                      setNotificationstab(2);
                    }}
                  >
                    Approvals - {approvalsCount}
                  </div>
                </div>
              </div>
              {notifications.length > 0 ? (
                <div className="px-4 h-[21.5rem] overflow-y-scroll">
                  {notifications
                    .sort((a, b) => {
                      const d1 = new Date(a.createdat);
                      const d2 = new Date(b.createdat);
                      return d2 - d1;
                    })
                    .map((n) => {
                      return (
                        <div
                          onClick={() => {
                            handleNotificationClick(n);
                          }}
                          className="mb-[8px] border cursor-pointer group transition-all hover:border-web-blue py-5 px-6 rounded-lg"
                        >
                          <div className="flex justify-between items-center">
                            <p className="font-semibold group-hover:text-web-blue">
                              {n.notificationtitle}
                            </p>
                            <p className="text-xs text-[#444D63] font-semibold">
                              {new Date(n.createdat).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  timeZone: "Asia/Kolkata",
                                },
                              )}
                            </p>
                          </div>
                          <p className="text-xs mt-[6px] font-semibold">
                            {n.message}
                          </p>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div>
                  <div className="flex h-[21.5rem] justify-center w-full items-center">
                    <div>
                      <img className="h-32 mx-auto" src={notification}></img>
                      <p className="text-center mt-2 font-semibold">
                        Your to-do list is clear. Stay tuned for you next task.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <HiringPerformance
              departments={departments}
              hiringManagers={hiringManagers}
              performanceData={hiringPerformanceData}
              userPrivilege={userPrivilege}
            />
          </div>
        </div>
        <div className="flex space-x-6  h-full  w-full">
          <div className=" bg-white w-2/5 py-3 items-center  rounded-divRadius shadow-sh">
            <div className="flex items-center justify-between w-full">
              <h2 className="font-semibold  relative  text-[16px] pl-4 leading-[18.75px] before:block before:bg-blue-500 before:w-1 before:content-'' before:absolute before:left-0 before:top-1/2 before:h-2/4 before:mr-4 before:transform before:-translate-y-1/2">
                Application Source
              </h2>
              <div className="flex justify-end space-x-3 pr-4">
                <Select
                  placeholder={<div>Select department</div>}
                  defaultValue={defaultDepartment}
                  options={departments}
                  onChange={(option) => {
                    setSourceState((prev) => {
                      return {
                        department: option.value,
                        hiringManager: prev.hiringManager,
                      };
                    });
                  }}
                  className="text-sm"
                ></Select>
              </div>
            </div>
            <div className="bg-gray-300 h-[1px] mt-3 w-full"></div>
            <div className="h-full flex flex-col justify-center">
              {dashboardMetrics?.source_metrics?.length > 0 ? (
                <div className="px-4 -mt-5 flex items-center">
                  <ReactApexChart
                    key={`source-donut-${sortBy?.value ?? "default"}-${companyId ?? ""}-${dashboardMetricsKey}`}
                    options={donutChart.options}
                    series={donutChart.series}
                    type="donut"
                  />

                  <div className="space-y-1 text-sm pt-2">
                    {sourceDonutLegends.map((value, index) => {
                      if (!donutChart.series[index]) {
                        return <></>;
                      }
                      return (
                        <div
                          className="flex items-center space-x-2"
                          key={index}
                        >
                          <div
                            style={{ backgroundColor: value.color }}
                            className="p-2 sm:p-2 md:p-2.5 rounded-lg"
                          ></div>
                          <p className="">{value.label}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex h-[20rem] justify-center w-full items-center">
                    <div>
                      <img className="h-28 mx-auto" src={conversion}></img>
                      <p className="text-center text-sm mt-2 font-semibold">
                        Sourcing metrics will populate as you begin adding
                        candidates.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white w-1/5 py-3 items-center rounded-divRadius shadow-sh">
            <div className="flex items-center justify-between w-full">
              <h2 className="font-semibold relative text-[16px] pl-4 leading-[18.75px] before:block before:bg-blue-500 before:w-1 before:content-'' before:absolute before:left-0 before:top-1/2 before:h-2/4 before:mr-4 before:transform before:-translate-y-1/2">
                Diversity
              </h2>
              <div className="flex items-center gap-2 m-1.5">
                <span
                  className={`text-base transition-colors duration-300 ${!isHired ? "font-bold" : "font-medium"
                    }`}
                >
                  Offers
                </span>
                <Switch
                  checked={isHired}
                  onChange={setIsHired}
                  height={24}
                  width={48}
                  handleDiameter={22}
                  offColor="#FFFFFF"
                  onColor="#FFFFFF"
                  onHandleColor="#4FABFF"
                  offHandleColor="#4FABFF"
                  uncheckedIcon={false}
                  checkedIcon={false}
                  boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1)"
                  handleColor="#8F94A1"
                  className="border border-[#8F94A1] rounded-full"
                />
                <span
                  className={`text-base transition-colors duration-300 ${isHired ? "font-bold" : "font-medium"
                    }`}
                >
                  Hires
                </span>
              </div>
            </div>
            <div className="bg-gray-300 h-[1px] mt-3 w-full"></div>
            <div className="h-full  flex flex-col justify-center items-center">
              {genderRatio.length > 0 &&
                !genderRatio.every((value) => value === 0) ? (
                <ReactApexChart
                  options={monochromeDonutChart.options}
                  series={monochromeDonutChart.series}
                  type="donut"
                  width={300}
                />
              ) : (
                <div className="flex flex-col h-fit justify-center w-full items-center">
                  <img
                    className="h-[160px] w-auto mx-auto"
                    src={diversity}
                    alt=""
                  ></img>
                  <p className="text-center text-sm mt-2 font-semibold">
                    No diversity data yet.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className=" bg-white w-2/5 py-3 items-center  rounded-divRadius shadow-sh">
            <div className="flex items-center justify-between w-full">
              <h2 className="font-semibold  relative  text-[16px] pl-4 leading-[18.75px] before:block before:bg-blue-500 before:w-1 before:content-'' before:absolute before:left-0 before:top-1/2 before:h-2/4 before:mr-4 before:transform before:-translate-y-1/2">
                Jobs Aging
              </h2>
              <div className="flex justify-end space-x-3 pr-4">
                <Select
                  placeholder={<div>Select department</div>}
                  defaultValue={defaultDepartment}
                  options={departments}
                  onChange={(option) => {
                    setAgingState((prev) => {
                      return {
                        department: option.value,
                        hiringManager: prev.hiringManager,
                      };
                    });
                  }}
                  className="text-sm"
                ></Select>
                {userPrivilege !== "Hiring Manager" && (
                  <Select
                    className="text-sm"
                    placeholder={<div>Select hiring manager</div>}
                    defaultValue={defaultManagers}
                    onChange={(option) => {
                      setAgingState((prev) => {
                        return {
                          department: prev.department,
                          hiringManager: option.value,
                        };
                      });
                    }}
                    options={hiringManagers}
                  ></Select>
                )}
              </div>
            </div>
            <div className="bg-gray-300 h-[1px] mt-3 w-full"></div>
            <div className="h-full flex flex-col justify-center">
              {Object.values(counts).some((v) => v > 0) ? (
                <ReactApexChart
                  options={Chart.options}
                  series={Chart.series}
                  type="bar"
                  height={250}
                />
              ) : (
                <div>
                  <div className="flex h-[20rem] justify-center w-full items-center">
                    <div>
                      <img className="h-28 mx-auto" src={aging}></img>
                      <p className="text-center text-sm mt-2 font-semibold">
                        No data on job aging.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
